import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import PageHero from "../components/PageHero";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UsersRound, XCircle, ShieldCheck, ArrowRight, Plus, Crown } from "lucide-react";

const TEAL = "#0D9488";

/** Admin-wide view of every group on the platform, with dissolve. */
export default function AdminGroups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/groups/admin/all");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const dissolve = async (g) => {
    if (!window.confirm(`Dissolve "${g.name}"? Members will lose access. This can't be undone.`)) return;
    setBusyId(g.id);
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success("Group dissolved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const active = items.filter((g) => !g.is_dissolved && !g.is_closed);
  const closed = items.filter((g) => g.is_closed && !g.is_dissolved);
  const dissolved = items.filter((g) => g.is_dissolved);

  return (
    <AppLayout>
      <PageHero
        eyebrow="Admin"
        title="All Groups"
        subtitle="Every group on the platform. Read anything, dissolve anything, or create one on behalf of a coach."
        icon={<ShieldCheck className="w-8 h-8 text-[#0D9488]" />}
        gradient
        actions={
          <Button
            onClick={() => setOpenCreate(true)}
            className="text-white"
            style={{ background: TEAL }}
            data-testid="admin-groups-new"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New group for a coach
          </Button>
        }
      />

      {loading ? (
        <Card className="p-6 h-24 animate-pulse bg-white" />
      ) : items.length === 0 ? (
        <Card className="p-8 text-center bg-white border-dashed border-slate-200 rounded-xl" data-testid="admin-groups-empty">
          <UsersRound className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <div className="text-slate-500 text-sm">No groups have been created yet.</div>
        </Card>
      ) : (
        <div className="space-y-8">
          <Section title={`Active (${active.length})`} items={active} onDissolve={dissolve} busyId={busyId} testid="admin-groups-active" />
          {closed.length > 0 && (
            <Section title={`Closed (${closed.length})`} items={closed} onDissolve={dissolve} busyId={busyId} testid="admin-groups-closed" />
          )}
          {dissolved.length > 0 && (
            <Section title={`Dissolved (${dissolved.length})`} items={dissolved} onDissolve={null} busyId={busyId} testid="admin-groups-dissolved" />
          )}
        </div>
      )}

      <AdminCreateGroupDialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        onCreated={() => { setOpenCreate(false); toast.success("Group created for the coach"); load(); }}
      />
    </AppLayout>
  );
}

function AdminCreateGroupDialog({ open, onClose, onCreated }) {
  const [coaches, setCoaches] = useState([]);
  const [coachId, setCoachId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/coaches/directory")
      .then(({ data }) => setCoaches(data?.coaches || []))
      .catch(() => setCoaches([]));
    setName(""); setDescription(""); setCoachId("");
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !coachId) return;
    setBusy(true);
    try {
      await api.post("/groups", {
        name: name.trim(),
        description: description.trim(),
        coach_user_id: coachId,
      });
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="admin-group-create-dialog">
        <DialogHeader>
          <DialogTitle>New group for a coach</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="admin-coach">Coach</Label>
            <select
              id="admin-coach"
              value={coachId}
              onChange={(e) => setCoachId(e.target.value)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm font-mono-num"
              required
              data-testid="admin-group-create-coach"
            >
              <option value="">Pick a verified coach…</option>
              {coaches.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.name || c.handle} · @{c.handle}
                </option>
              ))}
            </select>
            {coaches.length === 0 && (
              <div className="text-[11px] text-slate-400 mt-1">No verified coaches yet. Verify a coach first from Admin → Users.</div>
            )}
            <div className="text-[10px] text-slate-400 mt-1">This bypasses the "one active group" rule so admins can add extra groups when needed.</div>
          </div>
          <div>
            <Label htmlFor="admin-name">Group name</Label>
            <Input id="admin-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required data-testid="admin-group-create-name" />
          </div>
          <div>
            <Label htmlFor="admin-desc">Description</Label>
            <Textarea id="admin-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={600} data-testid="admin-group-create-desc" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !name.trim() || !coachId}
              className="text-white"
              style={{ background: TEAL }}
              data-testid="admin-group-create-submit"
            >
              {busy ? "Creating…" : "Create group"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, items, onDissolve, busyId, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-widest font-mono-num font-semibold text-slate-500 mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((g) => (
          <Card
            key={g.id}
            className={`p-4 border rounded-xl bg-white ${g.is_dissolved || g.is_closed ? "opacity-70 border-slate-200" : "border-slate-200"}`}
            data-testid={`admin-group-${g.id}`}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-white"
                style={{ background: "linear-gradient(135deg,#5EEAD4 0%,#0D9488 100%)" }}
              >
                <UsersRound className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-slate-900 truncate">{g.name}</div>
                  {g.is_closed && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-amber-100 text-amber-700">
                      Closed
                    </span>
                  )}
                  {g.is_dissolved && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-widest font-semibold bg-slate-200 text-slate-600">
                      Dissolved
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  Coach: {g.coach?.name || "—"} · {g.coach?.email || "—"}
                </div>
                <div className="text-[11px] text-slate-500 font-mono-num mt-1">
                  {g.member_count} members · {g.post_count} posts · created {g.created_at?.slice(0, 10)}
                </div>
                {g.description && <div className="text-sm text-slate-600 mt-2 line-clamp-2">{g.description}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <Link to={`/groups/${g.id}`} data-testid={`admin-group-open-${g.id}`} className="flex-1">
                <Button variant="outline" className="w-full text-teal-700 border-teal-300">
                  Open <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              {onDissolve && (
                <Button
                  variant="outline"
                  onClick={() => onDissolve(g)}
                  disabled={busyId === g.id}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  data-testid={`admin-group-dissolve-${g.id}`}
                >
                  <XCircle className="w-4 h-4 mr-1" /> {busyId === g.id ? "…" : "Dissolve"}
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
